import { useMemo, useSyncExternalStore } from 'react'

// Title and tagline change on every keystroke, but the builder only reads
// them when it saves. Kept outside its state, a keystroke re-renders the
// components that show them (inputs, live card, style action), not the whole
// builder with its leg search, regulation and surface pickers.

const EMPTY_COPY = Object.freeze({ title: '', titleSource: 'operator', subtitle: '', subtitleSource: 'operator' })

export function createPrepackCopyStore(initial = EMPTY_COPY) {
  let copy = { ...EMPTY_COPY, ...initial }
  const listeners = new Set()
  return {
    get: () => copy,
    set(patch) {
      const next = { ...copy, ...patch }
      if (Object.keys(next).every((key) => next[key] === copy[key])) return
      copy = next
      for (const listener of listeners) listener()
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

export function usePrepackCopy(store) {
  return useSyncExternalStore(store.subscribe, store.get)
}

/** Title and tagline as previewed and saved: trimmed, an empty title replaced by `defaultTitle`. */
export function resolvePackCopy({ title, subtitle }, defaultTitle) {
  return { title: title.trim() || defaultTitle, subtitle: subtitle.trim() }
}

/** `pack` with the store's current title and tagline, or null without a pack. */
export function useLivePack(store, pack, defaultTitle) {
  const { title, subtitle } = usePrepackCopy(store)
  return useMemo(() => (
    pack ? { ...pack, ...resolvePackCopy({ title, subtitle }, defaultTitle) } : null
  ), [defaultTitle, pack, subtitle, title])
}
